/**
 * `ManualInstallSteps` — ADR-0197 manual install path. The always-
 * visible two-step manual UI that sits BELOW the recommended one-click
 * action (or as the primary when no recommendation exists — the static /
 * custom, no-Cloudflare case).
 *
 * The founder was explicit about the shape: "some merchants prefer
 * manual, or have no Cloudflare access." So the manual path is NEVER
 * hidden behind a disclosure — it renders in every case, always the same
 * two steps.
 *
 * Step 1 — Add the script (browser agents + analytics)
 *   - The `<script>` snippet with a Copy button + an inline Verify
 *     control, driven by the shared `useProofCheck` state passed in via
 *     `proofCheck`. Mirrors `ScriptTagInstallPanel`'s
 *     `buildScriptSnippet` + inline-verify shape so pasting the snippet
 *     + clicking Verify satisfies the ADR-0043 proof primitive.
 *   - Frames the coverage honestly: this reaches agents that RUN JS +
 *     powers analytics. It does NOT cover static crawlers.
 *
 * Step 2 — Serve the static card (static crawlers)
 *   - A Download `agent-card.json` button (reuses `downloadDiscoveryJson`)
 *     + one-line copy: upload it to `/.well-known/agent-card.json` on
 *     your server.
 *   - The Shopify variant swaps the download for a link to the O2O
 *     guide, because Shopify reserves `/.well-known/` — the only way a
 *     Shopify store gets the standard well-known card is a Cloudflare
 *     Worker sitting in front of it (ADR-0197 §4).
 *
 * NO copy pretending Step 1 is complete on its own — the section header
 * says "manual install · always available," Step 1 says browser agents,
 * Step 2 says static crawlers. Both needed for full coverage. Precise,
 * technical, low-marketing (per CLAUDE.md §8).
 */

import { ArrowRight, Check, Copy, Download, RefreshCw } from 'lucide-react';
import { useState } from 'react';

import { useSites } from '../../app/providers.js';
import type { UseProofCheckState } from '../../hooks/useProofCheck.js';
import {
  buildScriptSnippet,
  downloadDiscoveryJson,
  isValidAgentCardKey,
} from '../../lib/install-snippet.js';
import { useC } from '../../lib/theme.js';
import { VERIFY_HINT } from './install-copy.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

export interface ManualInstallStepsProps {
  /** Shared proof-check state from the parent (`useProofCheck`, mounted
   *  once in `InstallVerifyStep`). Threads the per-site proof token into
   *  the snippet (via `data-proof-token`) AND drives the inline Verify
   *  control. Optional to preserve compile-time compatibility; without
   *  it the snippet renders without the proof-token attribute and no
   *  inline Verify button. */
  proofCheck?: UseProofCheckState;
  /** Shopify manual variant: Step 2 links the docs.sill.so O2O guide
   *  instead of offering a static-card download, because Shopify blocks
   *  `/.well-known/` — the standard path — and the only way to serve
   *  the card there is a Cloudflare Worker in front of the store
   *  (ADR-0197 §4). */
  variant?: 'default' | 'shopify';
}

export function ManualInstallSteps({
  proofCheck,
  variant = 'default',
}: ManualInstallStepsProps): JSX.Element | null {
  const C = useC();
  const { activeSite } = useSites();
  const [copied, setCopied] = useState(false);

  if (activeSite === null) return null;
  const site_key = activeSite.site_key ?? '';
  const has_key = isValidAgentCardKey(site_key);
  const proof_token = proofCheck?.data?.proof_token;
  const snippet = buildScriptSnippet(site_key, proof_token);

  const verifyDisabled =
    proofCheck === undefined
    || proofCheck.checking
    || proofCheck.verified
    || proofCheck.loading
    || proofCheck.data === null;

  const copy = (): void => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      void navigator.clipboard.writeText(snippet);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <div
      className="p-5"
      style={{
        border: `1px solid ${C.borderSoft}`,
        borderRadius: 2,
        background: C.surface,
      }}
      data-testid="install-model-manual"
    >
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          letterSpacing: 2,
          color: C.muted,
          marginBottom: 14,
        }}
      >
        MANUAL INSTALL · ALWAYS AVAILABLE
      </div>

      {/* Step 1 — Add the script */}
      <div className="mb-6" data-testid="manual-step-script">
        <div
          style={{
            fontFamily: sansFont,
            fontSize: 14,
            fontWeight: 500,
            color: C.text,
            marginBottom: 4,
          }}
        >
          Step 1 — Add the script
        </div>
        <p
          style={{
            fontFamily: sansFont,
            fontSize: 12.5,
            color: C.muted,
            lineHeight: 1.6,
            margin: '0 0 12px',
            maxWidth: 560,
          }}
        >
          Reaches browser agents that render your page and powers your
          traffic analytics. Paste before {'</body>'}.
        </p>
        <div
          className="relative p-4 mb-3"
          style={{
            background: C.surface2,
            border: `1px solid ${C.border}`,
            borderRadius: 2,
            opacity: has_key ? 1 : 0.45,
          }}
          data-testid="install-snippet-block"
        >
          <button
            type="button"
            onClick={copy}
            disabled={!has_key}
            aria-label="Copy snippet"
            className="absolute top-3 right-3 flex items-center gap-1.5 px-2 py-1"
            style={{
              background: C.bg,
              border: `1px solid ${C.border}`,
              fontFamily: monoFont,
              fontSize: 10,
              color: copied ? C.limeFg : C.muted,
              borderRadius: 2,
              cursor: has_key ? 'pointer' : 'default',
            }}
          >
            {copied ? <Check size={10} /> : <Copy size={10} />}
            {copied ? 'COPIED' : 'COPY'}
          </button>
          <pre
            style={{
              fontFamily: monoFont,
              fontSize: 11,
              color: C.text,
              lineHeight: 1.7,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              paddingRight: 60,
              margin: 0,
            }}
            data-proof-token={
              typeof proof_token === 'string' && proof_token.length > 0
                ? proof_token
                : undefined
            }
          >
            {snippet}
          </pre>
        </div>
        {proofCheck !== undefined ? (
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <button
              type="button"
              onClick={() => void proofCheck.verify()}
              disabled={verifyDisabled}
              className="flex items-center justify-center gap-2 px-5 py-2.5"
              style={{
                background: proofCheck.verified ? C.limeDim : C.lime,
                color: proofCheck.verified ? C.limeFg : C.ink,
                fontFamily: sansFont,
                fontWeight: 600,
                fontSize: 14,
                borderRadius: 2,
                opacity: verifyDisabled && !proofCheck.verified ? 0.6 : 1,
                border: proofCheck.verified ? `1px solid ${C.limeFg}` : 'none',
                cursor: verifyDisabled ? 'default' : 'pointer',
              }}
              aria-label={
                proofCheck.verified ? 'Domain verified' : 'Verify domain'
              }
              data-testid="manual-step-script-verify"
            >
              {proofCheck.checking ? (
                <>
                  <RefreshCw
                    size={15}
                    style={{ animation: 'spin 1.4s linear infinite' }}
                  />
                  Checking…
                </>
              ) : proofCheck.verified ? (
                <>
                  <Check size={15} /> Domain verified
                </>
              ) : (
                <>
                  Verify <ArrowRight size={15} />
                </>
              )}
            </button>
            <span
              style={{
                fontFamily: sansFont,
                fontSize: 12,
                color: C.muted,
                lineHeight: 1.5,
                maxWidth: 420,
              }}
            >
              {VERIFY_HINT}
            </span>
          </div>
        ) : null}
      </div>

      {/* Step 2 — Serve the static card */}
      <div data-testid="manual-step-card">
        <div
          style={{
            fontFamily: sansFont,
            fontSize: 14,
            fontWeight: 500,
            color: C.text,
            marginBottom: 4,
          }}
        >
          Step 2 — Serve the static card
        </div>
        <p
          style={{
            fontFamily: sansFont,
            fontSize: 12.5,
            color: C.muted,
            lineHeight: 1.6,
            margin: '0 0 12px',
            maxWidth: 560,
          }}
        >
          {variant === 'shopify' ? (
            <>
              Reaches static crawlers like ChatGPT. Shopify reserves{' '}
              <span style={{ fontFamily: monoFont }}>/.well-known/</span>{' '}
              — the standard path — so this step needs Cloudflare proxying
              your store&apos;s traffic.
            </>
          ) : (
            <>
              Reaches static crawlers like ChatGPT. Redirect{' '}
              <span style={{ fontFamily: monoFont }}>
                /.well-known/agent-card.json
              </span>{' '}
              to Sill — it never goes stale — or, if your host can&apos;t
              redirect, download the file and upload it there.
            </>
          )}
        </p>
        {variant === 'shopify' ? (
          <a
            href="https://docs.sill.so/discovery/shopify-cloudflare/"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 px-3 py-1.5"
            style={{
              background: 'transparent',
              border: `1px solid ${C.border}`,
              color: C.text,
              fontFamily: sansFont,
              fontSize: 12,
              borderRadius: 2,
              textDecoration: 'none',
            }}
            data-testid="manual-step-card-shopify-guide"
          >
            Read the Shopify + Cloudflare guide <ArrowRight size={12} />
          </a>
        ) : (
          <button
            type="button"
            onClick={() =>
              downloadDiscoveryJson({
                name: activeSite.name,
                url: `https://${activeSite.domain}`,
                site_key,
              })
            }
            disabled={!has_key}
            className="inline-flex items-center gap-2 px-3 py-1.5"
            style={{
              background: 'transparent',
              border: `1px solid ${C.border}`,
              color: C.text,
              fontFamily: sansFont,
              fontSize: 12,
              borderRadius: 2,
              cursor: has_key ? 'pointer' : 'default',
              opacity: has_key ? 1 : 0.5,
            }}
            data-testid="manual-step-card-download"
          >
            <Download size={12} /> Download agent-card.json
          </button>
        )}
      </div>
    </div>
  );
}
